<!-- src/components/PowerLogoutIcon.vue -->
<script setup>
import { ref, watch, onMounted, useAttrs } from "vue";
import { sysConfig } from "~shared/config/system.cfg.js";

defineOptions({
  inheritAttrs: false
});

const attrs = useAttrs();
const emit = defineEmits(["click"]);

const props = defineProps({
  progress: Number, // expected to be 0 → 1
  size: { type: Number, default: 30 }
});

const hand = ref(null);
const glowColor = ref("#666");
const rotationDeg = ref(0);
const time = ref("");

/* -------------------------------------------------------
   Color thresholds
------------------------------------------------------- */
function getColor(p) {
  if (p >= 90) return "#d9534f"; // red
  if (p >= 70) return "#f0ad4e"; // orange
  if (p >= 50) return "#5cb85c"; // green
  if (p >= 20) return "#b7b7b7";
  return "#666";
}

/* -------------------------------------------------------
   Converts seconds → "MM:SS"
------------------------------------------------------- */
function secondsToTimeString(seconds) {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return (
    String(minutes).padStart(2, "0") +
    ":" +
    String(secs).padStart(2, "0")
  );
}

/* -------------------------------------------------------
   Update SVG + Logout-Time
------------------------------------------------------- */
function updateSvg(progress) {
  if (!hand.value) return;

  let val = typeof progress === "object" ? progress?.value : progress;
  val = Number(val) || 0;

  const pct = Math.min(Math.max(val * 100, 0), 100);

  let deg = (pct * 360) / 100;
  if (deg < 36) deg = 0;
  rotationDeg.value = deg;

  const color = getColor(pct);
  glowColor.value = color;
  hand.value.setAttribute("fill", color);

  const totalSeconds = sysConfig.idleDelayMs / 1000;
  const secondsLeft = totalSeconds - val * totalSeconds;
  time.value = secondsToTimeString(secondsLeft);

  hand.value.style.animation =
    pct >= 90 ? "pulseGlow 1s infinite alternate" : "none";
}

/* -------------------------------------------------------
   Reactivity
------------------------------------------------------- */
watch(
  () => props.progress,
  updateSvg,
  { immediate: true }
);

onMounted(() => updateSvg(props.progress));

/* -------------------------------------------------------
   Click / Keyboard Activation
------------------------------------------------------- */
function handleActivate(e) {
  if (e.type === "click") return emit("click");

  if (e.type === "keydown" && (e.key === "Enter" || e.key === " ")) {
    e.preventDefault();
    emit("click");
  }
}
</script>

<template>
  <!-- Shows EXACT time left before logout -->
  <span class="nz-logout-time">{{ time }}</span>

  <svg
    v-bind="attrs"
    :width="size"
    :height="size"
    viewBox="0 0 80 80"
    tabindex="0"
    role="button"
    @click="handleActivate"
    @keydown="handleActivate"
    :style="{ transform: `rotate(${rotationDeg}deg)` }"
  >
    <defs>
      <filter id="glowFilter">
        <feFlood :flood-color="glowColor" flood-opacity="1" />
        <feComposite in2="SourceGraphic" operator="in" />
        <feGaussianBlur stdDeviation="2" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <g>
      <circle cx="40" cy="40" r="40" fill="transparent" />
      <circle
        cx="40"
        cy="40"
        r="32.7"
        stroke="#000"
        stroke-width="4"
        fill="none"
      />

      <path
        d="M40,41.8c-1.7,0-3-.9-3-2V3.6c0-1.1,1.4-2,3-2s3,.9,3,2v36.2c0,1.1-1.4,2-3,2Z"
        fill="none"
        stroke="#fff"
        stroke-width="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />

      <path
        ref="hand"
        filter="url(#glowFilter)"
        d="M40,41.8c-1.7,0-3-.9-3-2V3.6c0-1.1,1.4-2,3-2s3,.9,3,2v36.2c0,1.1-1.4,2-3,2Z"
      />
    </g>
  </svg>
</template>

<style lang="less" scoped>
@import (reference) '@/styles/variables.less';
.logout-icon {
  cursor: pointer;
  transform-origin: 50% 50%;
  transition: transform 0.6s cubic-bezier(0.65, 0, 1, 1);
  position: absolute;
  top: 16px;
  right: 10px;

}
.logout-icon:hover{
  transform: rotate(360deg) !important;
  path{
    fill: @danger;
  }

}

.nz-logout-time{
  position: absolute;
  color: #ccc;
  width: 45px;
  top:  42px;
  right: 0px;
  font-size: 14px;
  text-align: left;

}
@keyframes pulseGlow {
  from {
    opacity: 1;
  }
  to {
    opacity: 0.6;
  }
}
.logout-icon * {
  pointer-events: none;
}
</style>
